import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useTexture, Html } from '@react-three/drei';
import * as THREE from 'three';

const Earth = () => {
  const earthMap = useTexture('/textures/earth.jpg');
  const meshRef = useRef<THREE.Mesh>(null);
  
  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.5;
    }
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[1.5, 64, 64]} />
      <meshStandardMaterial map={earthMap} roughness={0.8} />
      
      {/* Auroras at poles */}
      <mesh position={[0, 1.5, 0]} rotation={[Math.PI/2, 0, 0]}>
        <torusGeometry args={[0.6, 0.1, 16, 32]} />
        <meshBasicMaterial color="#34D399" transparent opacity={0.6} blending={THREE.AdditiveBlending} />
      </mesh>
      <mesh position={[0, -1.5, 0]} rotation={[Math.PI/2, 0, 0]}>
        <torusGeometry args={[0.6, 0.1, 16, 32]} />
        <meshBasicMaterial color="#34D399" transparent opacity={0.6} blending={THREE.AdditiveBlending} />
      </mesh>
    </mesh>
  );
};

const Magnetosphere = () => {
  // Generate field lines that are compressed on the left (sunward) and stretched on the right
  const lines = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const pts = [];
      for (let t = 0; t <= Math.PI; t += 0.1) {
        // Dipole equation modified for solar wind compression
        // r = a * sin^2(t)
        // We'll manually construct a teardrop shape
        let x = Math.sin(t) * 4;
        let y = Math.cos(t) * 4;
        let z = 0;
        
        // Compress left side (x < 0), stretch right side (x > 0)
        if (x < 0) {
          x *= 0.6; // Compressed facing sun
        } else {
          x *= 2.5; // Stretched tail
        }
        
        // Rotate around Y axis to make a 3D cage
        const rotatedX = x * Math.cos(angle) - z * Math.sin(angle);
        const rotatedZ = x * Math.sin(angle) + z * Math.cos(angle);
        
        pts.push(new THREE.Vector3(rotatedX, y, rotatedZ));
      }
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      arr.push(geo);
    }
    return arr;
  }, []);

  return (
    <group>
      {lines.map((geo, idx) => (
        <line key={idx} geometry={geo}>
          <lineBasicMaterial color="#34D399" transparent opacity={0.3} />
        </line>
      ))}
    </group>
  );
};

const SolarWind = () => {
  const count = 300;
  const particlesRef = useRef<THREE.Points>(null);
  
  const initialData = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const types = new Float32Array(count); // 0 = miss, 1 = hit north, 2 = hit south
    for(let i=0; i<count; i++) {
      pos[i*3] = -15 - Math.random() * 10; // start far left
      pos[i*3+1] = (Math.random() - 0.5) * 8; 
      pos[i*3+2] = (Math.random() - 0.5) * 8; 
      
      const r = Math.random();
      if (r < 0.05) types[i] = 1; // North pole
      else if (r < 0.1) types[i] = 2; // South pole
      else types[i] = 0; // Miss
    }
    return { pos, types };
  }, []);

  const particlesGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(initialData.pos, 3));
    return geo;
  }, [initialData]);

  useFrame((state, delta) => {
    if (!particlesRef.current) return;
    const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
    
    for(let i=0; i<count; i++) {
      let x = positions[i*3];
      let y = positions[i*3+1];
      let z = positions[i*3+2];
      const type = initialData.types[i];
      
      const speed = 8 * delta;
      
      if (x < -4) {
        // Straight approach
        x += speed;
      } else {
        // Interacting with magnetosphere
        if (type === 0) {
          // Curve around
          x += speed;
          if (y > 0) y += speed * 0.5;
          else y -= speed * 0.5;
        } else if (type === 1) {
          // North pole funnel
          x += speed * 0.5;
          y += (1.5 - y) * 0.1; // Converge to y=1.5
          z -= z * 0.1;
        } else if (type === 2) {
          // South pole funnel
          x += speed * 0.5;
          y += (-1.5 - y) * 0.1; // Converge to y=-1.5
          z -= z * 0.1;
        }
      }
      
      // Reset if passed earth
      if (x > 10 || (type !== 0 && x > 0)) {
        x = -15 - Math.random() * 5;
        y = (Math.random() - 0.5) * 8;
        z = (Math.random() - 0.5) * 8;
      }
      
      positions[i*3] = x;
      positions[i*3+1] = y;
      positions[i*3+2] = z;
    }
    particlesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={particlesRef} geometry={particlesGeo}>
      <pointsMaterial color="#FDE047" size={0.15} transparent opacity={0.8} />
    </points>
  );
};

export const MagnetosphereAurora: React.FC<{ width?: number | string; height?: number | string }> = ({ width = '100%', height = 400 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: '100%', height: Number(height), borderRadius: '8px', overflow: 'hidden' }}>
        <Canvas camera={{ position: [0, 2, 12], fov: 45 }}>
          <ambientLight intensity={0.2} />
          {/* Sun from left */}
          <directionalLight position={[-10, 0, 0]} intensity={1.5} />
          
          <Earth />
          <Magnetosphere />
          <SolarWind />
          
          {/* Labels */}
          <Html position={[0, 2.5, 0]} center>
            <div style={{ color: '#34D399', fontWeight: 'bold', textShadow: '0 0 4px black', pointerEvents: 'none' }}>Aurora</div>
          </Html>
          <Html position={[0, -2.5, 0]} center>
            <div style={{ color: '#34D399', fontWeight: 'bold', textShadow: '0 0 4px black', pointerEvents: 'none' }}>Aurora</div>
          </Html>
          <Html position={[2, 5, 0]} center>
            <div style={{ color: '#34D399', opacity: 0.8, textShadow: '0 0 4px black', pointerEvents: 'none', whiteSpace: 'nowrap' }}>Magnetosphere Shield</div>
          </Html>
          <Html position={[-6, 2, 0]} center>
            <div style={{ color: '#FDE047', fontWeight: 'bold', textShadow: '0 0 4px black', pointerEvents: 'none' }}>Solar Wind</div>
          </Html>
          
          <OrbitControls enableZoom={true} enablePan={false} />
        </Canvas>
      </div>
    </div>
  );
};
